/**
 * ChannelPicker renderer · iter303aj
 *
 * Smart 3-channel picker (Email · SMS · Postal) for Notify Client.
 * Auto-selects channels based on what's available on the client record:
 *   • Email checkbox on if client.email exists
 *   • SMS checkbox on if client.phone_mobile exists
 *   • Postal checkbox on if client has NO email (with amber "ask client
 *     for email before mailing" prompt)
 * Operator can toggle any combination. Writes to captured_data.notify_channels.
 */
import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Printer, AlertTriangle } from 'lucide-react';

export default function ChannelPicker({ action, capturedData, onNext, onBack, onCancel, canBack, onChange }) {
  const params = action.params || {};
  const client = capturedData?.client_record || {};
  const hasEmail  = !!(client.email || '').trim();
  const hasMobile = !!(client.phone_mobile || '').trim();

  const initial = capturedData?.[params.write_to || 'notify_channels'] || null;
  const [email, setEmail] = useState(initial ? initial.includes('email') : hasEmail);
  const [sms, setSms]     = useState(initial ? initial.includes('sms')   : hasMobile);
  const [postal, setPostal] = useState(initial ? initial.includes('postal') : !hasEmail);

  // Live-emit selection so runner has it ready on advance.
  useEffect(() => {
    const arr = [];
    if (email)  arr.push('email');
    if (sms)    arr.push('sms');
    if (postal) arr.push('postal');
    onChange && onChange(arr);
  }, [email, sms, postal]);  // eslint-disable-line react-hooks/exhaustive-deps

  const noneChecked = !email && !sms && !postal;

  return (
    <div data-testid="wf-channel-picker">
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-600 font-semibold mb-2">
          <Mail size={12} /> Choose Notification Channels
        </div>
        <div className="text-slate-700 text-sm">
          {params.prompt || 'How should we notify the client? Auto-selected based on their contact details.'}
        </div>
      </div>

      <div className="mb-5 space-y-3">
        {/* Email */}
        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${email ? 'border-sky-400 bg-sky-50/50' : 'border-slate-200 bg-white hover:border-sky-200'}`}
          data-testid="wf-channel-email">
          <input
            type="checkbox"
            checked={email}
            onChange={e => setEmail(e.target.checked)}
            disabled={!hasEmail}
            data-testid="wf-channel-email-checkbox"
            className="mt-1"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              <Mail size={14} className="text-sky-500" /> Email
              {hasEmail ? (
                <span className="text-xs text-slate-500 font-normal">→ {client.email}</span>
              ) : (
                <span className="text-xs text-rose-500 font-normal">(no email on file)</span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Auth Letter + Confirmation Letter attached as PDFs.
            </div>
          </div>
        </label>

        {/* SMS */}
        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${sms ? 'border-purple-400 bg-purple-50/50' : 'border-slate-200 bg-white hover:border-purple-200'}`}
          data-testid="wf-channel-sms">
          <input
            type="checkbox"
            checked={sms}
            onChange={e => setSms(e.target.checked)}
            disabled={!hasMobile}
            data-testid="wf-channel-sms-checkbox"
            className="mt-1"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              <MessageSquare size={14} className="text-purple-500" /> SMS
              {hasMobile ? (
                <span className="text-xs text-slate-500 font-normal">→ {client.phone_mobile}</span>
              ) : (
                <span className="text-xs text-rose-500 font-normal">(no mobile on file)</span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Short confirmation SMS (single-message length).
            </div>
          </div>
        </label>

        {/* Postal */}
        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${postal ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200 bg-white hover:border-amber-200'}`}
          data-testid="wf-channel-postal">
          <input
            type="checkbox"
            checked={postal}
            onChange={e => setPostal(e.target.checked)}
            data-testid="wf-channel-postal-checkbox"
            className="mt-1"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              <Printer size={14} className="text-amber-600" /> Postal (Print + Mail)
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Downloads a print bundle for you to physically mail out.
            </div>
          </div>
        </label>
      </div>

      {!hasEmail && (
        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2"
          data-testid="wf-channel-no-email-banner">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">No email on file</div>
            <div className="text-xs mt-1">
              Before mailing, please ask the client for an email address on the next call —
              it&apos;s faster + keeps the paper trail digital. If they refuse, Postal only is fine.
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={onNext}
          disabled={noneChecked}
          data-testid="wf-channel-continue-btn"
          className="px-6 py-2 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700 disabled:opacity-40">
          {noneChecked ? 'Pick at least one channel' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
